import React, { useState, useEffect, useRef } from 'react';
import { DOC_REQUEST_ITEM_STATUS } from '../constants/statuses';

const BATCH_SIZE = 3;

export default function ClientUpload() {
  // ── Token + base state ──
  const [token, setToken] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [status, setStatus] = useState('loading');

  // ── Request mode state (new) ──
  const [requestMode, setRequestMode] = useState(false);
  const [requestData, setRequestData] = useState(null);   // document_requests row
  const [requestItems, setRequestItems] = useState([]);   // document_request_items rows

  // Per-item working state keyed by item.id
  //   itemFiles:        { [itemId]: File[] (staged locally, not yet uploaded) }
  //   itemDragOver:     { [itemId]: boolean }
  //   itemNotReadyOpen: { [itemId]: boolean }   (whether the "Not Ready" panel is open)
  //   itemNotReadyQuick:{ [itemId]: string }    (quick-pick value)
  //   itemNotReadyDate: { [itemId]: string }    (YYYY-MM-DD)
  //   itemBusy:         { [itemId]: string | false } (label of in-flight action)
  const [itemFiles, setItemFiles] = useState({});
  const [itemDragOver, setItemDragOver] = useState({});
  const [itemNotReadyOpen, setItemNotReadyOpen] = useState({});
  const [itemNotReadyQuick, setItemNotReadyQuick] = useState({});
  const [itemNotReadyDate, setItemNotReadyDate] = useState({});
  const [itemBusy, setItemBusy] = useState({});
  const [itemError, setItemError] = useState({});

  // ── Generic mode state (legacy, untouched) ──
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: '' });
  const [comment, setComment] = useState('');

  const fileInputRef = useRef();

  // ── Validate token + optionally detect request mode ──
  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const t = pathParts[pathParts.length - 1];
    setToken(t);
    if (t) validateToken(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // POST helper for the public upload endpoints (Batch 10). The anon key no longer touches
  // upload_tokens / document_requests / document_request_items / file_exchange directly — every
  // one of those calls goes through a service-key endpoint that validates the token server-side.
  async function apiPost(path, body) {
    const r = await fetch(`/api/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
    return data;
  }

  async function validateToken(t) {
    try {
      const data = await apiPost('upload-validate', { token: t });
      if (!data.valid) {
        setStatus(data.reason === 'inactive' ? 'inactive' : data.reason === 'expired' ? 'expired' : 'notfound');
        return;
      }
      setTokenData(data.token);   // sanitized: no client_id/property_id (server derives those)
      if (data.mode === 'request' && data.request) {
        setRequestMode(true);
        setRequestData(data.request);
        setRequestItems(data.items || []);
      }
      setStatus('valid');
    } catch {
      setStatus('notfound');   // unexpected error validating — render a real state
    }
  }

  // ── Upload one file: sign → PUT direct to storage → record ──
  // Bytes go straight to storage via the signed URL (never through a function → no 4.5MB cap).
  // The endpoint derives client_id/property_id from the TOKEN, so nothing here can target another
  // client. Pass itemId (+ itemStatus) to link+mark a request item in the same record call.
  async function uploadOneFile(file, sessionId, { fileType, itemId, itemStatus } = {}) {
    const sign = await apiPost('upload-sign', { token, fileName: file.name, sessionId, content_type: file.type });
    const ct = sign.content_type || file.type || 'application/octet-stream';
    const putResp = await fetch(sign.signedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': ct } });
    if (!putResp.ok) throw new Error(`upload failed: ${putResp.status}`);
    const rec = await apiPost('upload-record', {
      token,
      storagePath: sign.storagePath,
      fileName: file.name,
      fileSize: file.size,
      sessionId,
      fileType: fileType || null,
      itemId: itemId || undefined,
      itemStatus: itemStatus || undefined,
    });
    return { fileExchangeId: rec.fileExchangeId };
  }

  // No-file request-item status change (na / fulfilled-shortcut / not_ready / pending reset).
  async function recordItemStatus(itemId, itemStatus, extra = {}) {
    return apiPost('upload-record', { token, itemId, itemStatus, ...extra });
  }

  // ─────────────────────────────────────────────────────────────────────
  // REQUEST MODE — per-item actions
  // ─────────────────────────────────────────────────────────────────────

  // Drop / select files on a specific item's drop zone
  function stageFilesForItem(itemId, fileList) {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setItemFiles(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), ...arr] }));
    setItemError(prev => ({ ...prev, [itemId]: null }));
  }

  function removeStagedFile(itemId, idx) {
    setItemFiles(prev => {
      const arr = [...(prev[itemId] || [])];
      arr.splice(idx, 1);
      return { ...prev, [itemId]: arr };
    });
  }

  // Upload staged files for this item, mark item fulfilled
  async function uploadFilesForItem(item) {
    const staged = itemFiles[item.id] || [];
    if (staged.length === 0) return;
    setItemBusy(prev => ({ ...prev, [item.id]: 'Uploading' }));
    setItemError(prev => ({ ...prev, [item.id]: null }));
    try {
      const sessionId = `req_${requestData.id}_item_${item.id}_${Date.now()}`;
      // The FIRST file carries itemId+status so record() links it and marks the item fulfilled
      // (+ bumps the token counter); subsequent files just add file_exchange rows.
      let firstFxId = null;
      for (let idx = 0; idx < staged.length; idx++) {
        const res = await uploadOneFile(staged[idx], sessionId, {
          fileType: item.document_type,             // lets the staff intake auto-suggest the folder
          itemId: idx === 0 ? item.id : undefined,
          itemStatus: idx === 0 ? 'fulfilled' : undefined,
        });
        if (idx === 0) firstFxId = res.fileExchangeId;
      }

      setRequestItems(prev => prev.map(it =>
        it.id === item.id
          ? { ...it, status: 'fulfilled', file_exchange_id: firstFxId, fulfilled_at: new Date().toISOString() }
          : it
      ));
      setItemFiles(prev => ({ ...prev, [item.id]: [] }));
    } catch (e) {
      setItemError(prev => ({ ...prev, [item.id]: e.message || 'Upload failed' }));
    }
    setItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // Mark the item as N/A (doesn't apply)
  async function markItemNA(item) {
    setItemBusy(prev => ({ ...prev, [item.id]: 'Saving' }));
    try {
      await recordItemStatus(item.id, 'na');
      setRequestItems(prev => prev.map(it =>
        it.id === item.id ? { ...it, status: 'na' } : it
      ));
    } catch (e) {
      setItemError(prev => ({ ...prev, [item.id]: 'Could not save. Please try again.' }));
    }
    setItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // Mark Complete — the client asserts this item is resolved without uploading a file
  // (e.g., they already sent it outside this system). Sets status=fulfilled.
  async function markItemComplete(item) {
    setItemBusy(prev => ({ ...prev, [item.id]: 'Saving' }));
    try {
      await recordItemStatus(item.id, 'fulfilled', { shortcut: true });
      setRequestItems(prev => prev.map(it =>
        it.id === item.id ? { ...it, status: 'fulfilled', shortcut_fulfilled: true } : it
      ));
    } catch (e) {
      setItemError(prev => ({ ...prev, [item.id]: 'Could not save. Please try again.' }));
    }
    setItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // Compute a YYYY-MM-DD date string from the quick-pick + manual date
  function resolveNotReadyDate(quick, manual) {
    if (quick === 'specific') return manual || '';
    const d = new Date();
    if (quick === '1w')  d.setDate(d.getDate() + 7);
    if (quick === '2w')  d.setDate(d.getDate() + 14);
    if (quick === '30d') d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }

  async function confirmNotReady(item) {
    const quick = itemNotReadyQuick[item.id];
    const manual = itemNotReadyDate[item.id];
    const resolved = resolveNotReadyDate(quick, manual);
    if (!resolved) {
      setItemError(prev => ({ ...prev, [item.id]: 'Please pick a date or a quick option.' }));
      return;
    }

    setItemBusy(prev => ({ ...prev, [item.id]: 'Saving' }));
    try {
      await recordItemStatus(item.id, 'not_ready', { promisedByDate: resolved });

      setRequestItems(prev => prev.map(it =>
        it.id === item.id ? { ...it, status: 'not_ready', promised_by_date: resolved } : it
      ));
      setItemNotReadyOpen(prev => ({ ...prev, [item.id]: false }));
      setItemError(prev => ({ ...prev, [item.id]: null }));
    } catch (e) {
      setItemError(prev => ({ ...prev, [item.id]: 'Could not save. Please try again.' }));
    }
    setItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // Revert an item back to pending (so client can change their mind)
  async function resetItem(item) {
    setItemBusy(prev => ({ ...prev, [item.id]: 'Saving' }));
    try {
      await recordItemStatus(item.id, 'pending');

      setRequestItems(prev => prev.map(it =>
        it.id === item.id
          ? { ...it, status: 'pending', promised_by_date: null, file_exchange_id: null, fulfilled_at: null, shortcut_fulfilled: false }
          : it
      ));
    } catch (e) {
      setItemError(prev => ({ ...prev, [item.id]: 'Could not reset.' }));
    }
    setItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // ─────────────────────────────────────────────────────────────────────
  // GENERIC MODE — unchanged from the original file
  // ─────────────────────────────────────────────────────────────────────

  function handleFileSelect(e) { addFiles(e.target.files); }
  function handleDrop(e) { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }
  function addFiles(list) { setFiles(prev => [...prev, ...Array.from(list)]); }
  function removeFile(i) { setFiles(prev => prev.filter((_, idx) => idx !== i)); }
  function formatSize(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1048576).toFixed(1)} MB`;
  }

  async function uploadFiles() {
    if (!files.length || !tokenData) return;
    setUploading(true);
    const allResults = [];
    const sessionId = Date.now().toString();
    const total = files.length;
    setProgress({ done: 0, total, current: '' });

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      setProgress(p => ({ ...p, current: `Batch ${Math.floor(i/BATCH_SIZE)+1} of ${Math.ceil(total/BATCH_SIZE)}` }));
      const batchResults = await Promise.all(batch.map(async (file) => {
        try { await uploadOneFile(file, sessionId); return { success: true, name: file.name }; }
        catch (e) { return { success: false, name: file.name, error: e.message }; }
      }));
      allResults.push(...batchResults);
      setProgress(p => ({ ...p, done: allResults.length }));
    }

    // record() bumps upload_tokens.files_received per file server-side — no client-side counter update.
    setUploaded(allResults);
    setUploading(false);
    setStatus('done');
  }

  const isConsultant = tokenData?.uploader_type === 'consultant';

  // ── Styles ─────────────────────────────────────────────────────
  const s = {
    page: { minHeight:'100vh', background:'var(--bg)', fontFamily:"'Public Sans', system-ui, sans-serif", display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px' },
    card: { background:'#ffffff', borderRadius:'14px', boxShadow:'0 4px 24px rgba(0,0,0,0.08)', maxWidth:'680px', width:'100%', overflow:'hidden', marginTop:'24px', marginBottom:'24px' },
    header: { background:'var(--ink)', padding:'24px 28px', display:'flex', alignItems:'center', gap:'14px' },
    headerText: { flex:1 },
    headerTitle: { fontSize:'16px', fontWeight:'700', color:'#ffffff', marginBottom:'2px' },
    headerSub: { fontSize:'12px', color:'rgba(180,0,0,0.8)' },
    body: { padding:'28px' },
    clientBadge: { background: isConsultant ? 'rgba(96,165,250,0.08)' : 'rgba(180,0,0,0.08)', border: `1px solid ${isConsultant ? 'rgba(96,165,250,0.2)' : 'rgba(180,0,0,0.2)'}`, borderRadius:'8px', padding:'12px 16px', marginBottom:'20px' },
    clientName: { fontSize:'15px', fontWeight:'700', color:'var(--ink)', marginBottom:'2px' },
    clientSub: { fontSize:'12px', color:'var(--slate)' },
    dropZone: (over) => ({ border:`2px dashed ${over ? 'var(--red)' : '#c8ccd8'}`, borderRadius:'12px', padding:'40px 24px', textAlign:'center', cursor:'pointer', transition:'all 0.2s', background: over ? 'rgba(180,0,0,0.04)' : '#fafbfd', marginBottom:'16px' }),
    dropZoneSmall: (over) => ({ border:`2px dashed ${over ? 'var(--red)' : '#c8ccd8'}`, borderRadius:'10px', padding:'18px 14px', textAlign:'center', cursor:'pointer', transition:'all 0.2s', background: over ? 'rgba(180,0,0,0.04)' : '#fafbfd', fontSize:'12px', color:'var(--slate)' }),
    dropZonePrimary: (over) => ({
      border: `2px dashed ${over ? 'var(--red-dark)' : 'rgba(180,0,0,0.5)'}`,
      borderRadius:'12px',
      padding:'26px 20px',
      textAlign:'center',
      cursor:'pointer',
      transition:'all 0.2s',
      background: over ? 'rgba(180,0,0,0.10)' : 'rgba(180,0,0,0.04)',
      marginBottom:'12px',
    }),
    dropIconLarge: { fontSize:'28px', marginBottom:'8px' },
    dropTitlePrimary: { fontSize:'14px', fontWeight:'700', color:'var(--ink)', marginBottom:'2px' },
    dropSubPrimary: { fontSize:'11px', color:'var(--slate)' },
    orLabel: { fontSize:'10px', color:'#9ca3af', fontWeight:'700', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px', marginTop:'4px' },
    itemActionsRow: { display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' },
    infoIcon: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:'14px', height:'14px', borderRadius:'50%', background:'#e4e7ef', color:'var(--slate)', fontSize:'9px', fontWeight:'700', marginLeft:'4px', cursor:'help' },
    dropIcon: { fontSize:'36px', marginBottom:'12px' },
    dropTitle: { fontSize:'15px', fontWeight:'600', color:'var(--ink)', marginBottom:'4px' },
    dropSub: { fontSize:'12px', color:'var(--slate)' },
    fileList: { display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px', maxHeight:'260px', overflowY:'auto' },
    fileItem: { display:'flex', alignItems:'center', gap:'10px', background:'#f5f7fb', borderRadius:'8px', padding:'10px 12px' },
    fileName: { flex:1, fontSize:'13px', color:'var(--ink)', fontWeight:'500', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
    fileSize: { fontSize:'11px', color:'var(--slate)', flexShrink:0 },
    removeBtn: { background:'none', border:'none', cursor:'pointer', color:'var(--slate)', fontSize:'16px', lineHeight:1, padding:'0 2px', flexShrink:0 },
    uploadBtn: { width:'100%', padding:'14px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'8px', color: 'white', fontSize:'14px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans', system-ui, sans-serif" },
    disabledBtn: { width:'100%', padding:'14px', background:'var(--line)', border:'none', borderRadius:'8px', color:'var(--slate)', fontSize:'14px', fontWeight:'700', cursor:'not-allowed', fontFamily:"'Public Sans', system-ui, sans-serif" },
    disclaimer: { fontSize:'11px', color:'var(--slate)', textAlign:'center', marginTop:'14px', lineHeight:'1.5' },
    commentBox: { width:'100%', padding:'10px 12px', border:'1px solid #c8ccd8', borderRadius:'8px', fontSize:'13px', color:'var(--ink)', fontFamily:"'Public Sans', system-ui, sans-serif", resize:'vertical', minHeight:'80px', outline:'none', boxSizing:'border-box', marginBottom:'16px' },
    commentLabel: { fontSize:'12px', fontWeight:'600', color:'#4a5068', marginBottom:'6px', display:'block' },

    // Request-mode styles
    reqIntro: { fontSize:'12px', color:'var(--red-dark)', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'6px' },
    reqTitle: { fontFamily:"'Archivo', sans-serif", fontSize:'20px', fontWeight:'700', color:'var(--ink)', marginBottom:'2px' },
    reqSub: { fontSize:'12px', color:'var(--slate)', marginBottom:'22px' },
    propertyCard: { background:'#fafbfd', border:'1px solid #e4e7ef', borderRadius:'12px', padding:'18px 20px', marginBottom:'18px' },
    propertyName: { fontSize:'16px', fontWeight:'700', color:'var(--ink)', marginBottom:'4px' },
    propertyMeta: { fontSize:'11px', color:'var(--red-dark)', fontFamily:"'DM Mono', monospace", marginBottom:'2px' },
    progressPill: { display:'inline-block', background:'rgba(180,0,0,0.12)', border:'1px solid rgba(180,0,0,0.3)', color:'var(--red-dark)', padding:'3px 10px', borderRadius:'20px', fontSize:'10px', fontWeight:'700', letterSpacing:'0.04em', textTransform:'uppercase' },
    itemRow: { background:'#ffffff', border:'1px solid #e4e7ef', borderRadius:'10px', marginBottom:'12px', overflow:'hidden' },
    itemHeader: { display:'flex', alignItems:'center', gap:'12px', padding:'14px 18px', borderBottom:'1px solid #eef1f8' },
    itemTitle: { flex:1, fontSize:'14px', fontWeight:'600', color:'var(--ink)' },
    itemAction: { fontSize:'11px', padding:'5px 11px', border:'1px solid #c8ccd8', borderRadius:'6px', background:'transparent', color:'#4a5068', fontWeight:'600', cursor:'pointer', fontFamily:"'Public Sans', system-ui, sans-serif", whiteSpace:'nowrap' },
    itemActionActive: { background:'linear-gradient(135deg,#B40000,#9C0000)', color:'#fff', border:'none' },
    itemBody: { padding:'14px 18px' },
    statusBadge: (color) => ({ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 11px', borderRadius:'20px', fontSize:'10px', fontWeight:'700', background:`rgba(${color},0.14)`, color:`rgb(${color === '34,197,94' ? '21,128,61' : color === '245,158,11' ? '180,83,9' : '100,116,139'})`, border:`1px solid rgba(${color},0.32)`, letterSpacing:'0.04em' }),
    notReadyPanel: { background:'linear-gradient(135deg, rgba(180,0,0,0.04), rgba(156,0,0,0.02))', border:'1px solid rgba(180,0,0,0.25)', borderRadius:'10px', padding:'14px 16px', marginBottom:'12px' },
    notReadyLabel: { fontSize:'10px', fontWeight:'700', color:'var(--red-dark)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'8px' },
    inputField: { padding:'7px 11px', border:'1px solid #c8ccd8', borderRadius:'6px', fontSize:'12px', fontFamily:"'Public Sans',system-ui,sans-serif", outline:'none', background:'#ffffff' },
    confirmBtn: { padding:'7px 14px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'6px', color:'#fff', fontSize:'11px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    linkBtn: { background:'none', border:'none', color:'#60a5fa', fontSize:'11px', cursor:'pointer', padding:0, fontFamily:"'Public Sans',system-ui,sans-serif", textDecoration:'underline' },
  };

  // ── Loading / error states ──
  if (status === 'loading') return (
    <div style={s.page}>
      <div style={{ textAlign:'center', color:'var(--slate)', marginTop:'80px' }}>
        <div style={{ fontSize:'24px', marginBottom:'8px' }}>⏳</div>
        <div>Validating your upload link...</div>
      </div>
    </div>
  );

  if (status === 'notfound') return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign:'center', padding:'48px 28px' }}>
        <div style={{ fontSize:'40px', marginBottom:'14px' }}>🔗</div>
        <div style={{ fontSize:'18px', fontWeight:'700', color:'var(--ink)', marginBottom:'8px' }}>Link Not Found</div>
        <div style={{ fontSize:'13px', color:'var(--slate)', lineHeight:'1.6' }}>This upload link isn't recognized — the address may be incomplete or mistyped.<br/>Check that you copied the full link, or contact your NTRG representative.</div>
      </div>
    </div>
  );

  if (status === 'inactive') return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign:'center', padding:'48px 28px' }}>
        <div style={{ fontSize:'40px', marginBottom:'14px' }}>🔒</div>
        <div style={{ fontSize:'18px', fontWeight:'700', color:'var(--ink)', marginBottom:'8px' }}>Link Deactivated</div>
        <div style={{ fontSize:'13px', color:'var(--slate)', lineHeight:'1.6' }}>This upload link has been turned off.<br/>Please contact your NTRG representative for a new link.</div>
      </div>
    </div>
  );

  if (status === 'expired') return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign:'center', padding:'48px 28px' }}>
        <div style={{ fontSize:'40px', marginBottom:'14px' }}>⏰</div>
        <div style={{ fontSize:'18px', fontWeight:'700', color:'var(--ink)', marginBottom:'8px' }}>Link Expired</div>
        <div style={{ fontSize:'13px', color:'var(--slate)', lineHeight:'1.6' }}>This upload link has expired.<br/>Please contact your NTRG representative to request a new link.</div>
      </div>
    </div>
  );

  if (status === 'done') return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <img src="/Wigley.png" alt="Wigley" style={{ width:"44px", height:"44px", objectFit:"contain", borderRadius:"10px" }} />
          <div style={s.headerText}>
            <div style={s.headerTitle}>WigleyHub · NTRG</div>
            <div style={s.headerSub}>National Tax Resource Group</div>
          </div>
        </div>
        <div style={{ padding:'40px 28px', textAlign:'center' }}>
          <div style={{ fontSize:'48px', marginBottom:'16px' }}>✅</div>
          <div style={{ fontSize:'20px', fontWeight:'700', color:'var(--ink)', marginBottom:'8px' }}>Documents Received</div>
          <div style={{ fontSize:'13px', color:'var(--slate)', marginBottom:'8px', lineHeight:'1.6' }}>
            {uploaded.filter(f=>f.success).length} of {uploaded.length} files uploaded successfully.
          </div>
          <div style={{ fontSize:'13px', color:'var(--slate)', marginBottom:'24px', lineHeight:'1.6' }}>
            Your documents have been securely received by NTRG.<br/>Your representative will be in touch shortly.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', textAlign:'left', background:'#f5f7fb', borderRadius:'8px', padding:'14px 16px', maxHeight:'260px', overflowY:'auto' }}>
            {uploaded.map((f, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px' }}>
                <span style={{ color: f.success ? '#34d399' : '#f87171' }}>{f.success ? '✓' : '✗'}</span>
                <span style={{ color: f.success ? 'var(--ink)' : '#f87171' }}>{f.name}</span>
                {!f.success && <span style={{ color:'#f87171', fontSize:'11px' }}>— {f.error}</span>}
              </div>
            ))}
          </div>
          {uploaded.some(f => !f.success) && (
            <div style={{ marginTop:'16px' }}>
              <button style={s.uploadBtn} onClick={()=>{ setFiles(uploaded.filter(f=>!f.success).map(f=>f.file)); setUploaded([]); setStatus('valid'); }}>
                Retry Failed Files
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════
  // REQUEST MODE — when token is linked to a document_request
  // ══════════════════════════════════════════════════════════════════════
  if (requestMode && requestData) {
    const totalItems = requestItems.length;
    const resolvedItems = requestItems.filter(i => i.status === DOC_REQUEST_ITEM_STATUS.FULFILLED || i.status === DOC_REQUEST_ITEM_STATUS.NA).length;
    const notReadyItems = requestItems.filter(i => i.status === DOC_REQUEST_ITEM_STATUS.NOT_READY).length;
    const pendingItems = requestItems.filter(i => i.status === DOC_REQUEST_ITEM_STATUS.PENDING).length;
    const allResolvedOrScheduled = pendingItems === 0;

    function renderItem(item) {
      const staged = itemFiles[item.id] || [];
      const isBusy = !!itemBusy[item.id];
      const err = itemError[item.id];
      const dragging = !!itemDragOver[item.id];
      const nrOpen = !!itemNotReadyOpen[item.id];
      const statusFulfilled = item.status === DOC_REQUEST_ITEM_STATUS.FULFILLED;
      const statusNA = item.status === DOC_REQUEST_ITEM_STATUS.NA;
      const statusNotReady = item.status === DOC_REQUEST_ITEM_STATUS.NOT_READY;
      const inActionableState = item.status === DOC_REQUEST_ITEM_STATUS.PENDING;

      return (
        <div key={item.id} style={s.itemRow}>
          <div style={s.itemHeader}>
            <div style={s.itemTitle}>
              {item.document_type}{item.other_description ? `: ${item.other_description}` : ''}
            </div>

            {/* Status badges for completed states */}
            {statusFulfilled && (
              <span style={s.statusBadge('34,197,94')}>
                {item.shortcut_fulfilled ? '✓ Already Provided' : '✓ Uploaded'}
              </span>
            )}
            {statusNA && (
              <span style={s.statusBadge('148,163,184')}>N/A</span>
            )}
            {statusNotReady && (
              <span style={s.statusBadge('245,158,11')}>
                Expected {item.promised_by_date ? new Date(item.promised_by_date + 'T00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
              </span>
            )}

            {/* "Change answer" for completed states */}
            {!inActionableState && (
              <button style={s.linkBtn} onClick={()=>resetItem(item)} disabled={isBusy}>Change</button>
            )}
          </div>

          {/* Body: drop zone is PRIMARY action, buttons below as alternatives */}
          {inActionableState && (
            <div style={s.itemBody}>
              {/* Prominent drop zone FIRST — this is what the client should do */}
              <div
                style={s.dropZonePrimary(dragging)}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.multiple = true;
                  input.onchange = e => stageFilesForItem(item.id, e.target.files);
                  input.click();
                }}
                onDragOver={e => { e.preventDefault(); setItemDragOver(prev => ({ ...prev, [item.id]: true })); }}
                onDragLeave={() => setItemDragOver(prev => ({ ...prev, [item.id]: false }))}
                onDrop={e => { e.preventDefault(); setItemDragOver(prev => ({ ...prev, [item.id]: false })); stageFilesForItem(item.id, e.dataTransfer.files); }}>
                <div style={s.dropIconLarge}>📤</div>
                <div style={s.dropTitlePrimary}>Upload {item.document_type}</div>
                <div style={s.dropSubPrimary}>Drag and drop here, or click to browse</div>
              </div>

              {/* Staged files list (appears after client drops/selects) */}
              {staged.length > 0 && (
                <>
                  <div style={{ display:'flex', flexDirection:'column', gap:'4px', marginTop:'6px', marginBottom:'10px' }}>
                    {staged.map((f, idx) => (
                      <div key={idx} style={s.fileItem}>
                        <span style={{ fontSize:'14px' }}>
                          {f.name.endsWith('.pdf') ? '📄' : f.name.match(/\.(xlsx?|csv)$/i) ? '📊' : f.name.match(/\.(docx?)$/i) ? '📝' : '📎'}
                        </span>
                        <span style={s.fileName}>{f.name}</span>
                        <span style={s.fileSize}>{formatSize(f.size)}</span>
                        <button style={s.removeBtn} onClick={()=>removeStagedFile(item.id, idx)}>×</button>
                      </div>
                    ))}
                  </div>
                  <button
                    style={{ ...s.uploadBtn, marginBottom:'12px', padding:'10px' }}
                    disabled={isBusy}
                    onClick={() => uploadFilesForItem(item)}>
                    {itemBusy[item.id] === 'Uploading' ? '⏳ Uploading...' : `⬆ Upload ${staged.length} file${staged.length !== 1 ? 's' : ''}`}
                  </button>
                </>
              )}

              {/* "Or" separator + alternative action buttons below */}
              <div style={s.orLabel}>Or, if you can't upload right now:</div>
              <div style={s.itemActionsRow}>
                <button
                  style={s.itemAction}
                  disabled={isBusy}
                  onClick={() => {
                    const ok = window.confirm(
                      `Are you sure? This tells NTRG that you've already provided the "${item.document_type}" document prior to this request.`
                    );
                    if (ok) markItemComplete(item);
                  }}>
                  {itemBusy[item.id] === 'Saving' ? '…' : 'Already Provided'}
                </button>
                <span
                  style={s.infoIcon}
                  title={`Use this only if you've already sent "${item.document_type}" to NTRG outside of WigleyHub (e.g., in a prior email).`}>
                  ⓘ
                </span>
                <button style={s.itemAction} disabled={isBusy} onClick={()=>markItemNA(item)}>
                  N/A
                </button>
                <button
                  style={{ ...s.itemAction, ...(nrOpen ? s.itemActionActive : {}) }}
                  disabled={isBusy}
                  onClick={() => setItemNotReadyOpen(prev => ({ ...prev, [item.id]: !prev[item.id] }))}>
                  Not Ready {nrOpen ? '▾' : '▸'}
                </button>
              </div>

              {/* Not Ready expand panel (appears inline when Not Ready clicked) */}
              {nrOpen && (
                <div style={{ ...s.notReadyPanel, marginTop:'10px' }}>
                  <div style={s.notReadyLabel}>Estimated Ready Date</div>
                  <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                    <select
                      style={s.inputField}
                      value={itemNotReadyQuick[item.id] || ''}
                      onChange={e => {
                        setItemNotReadyQuick(prev => ({ ...prev, [item.id]: e.target.value }));
                        if (e.target.value !== 'specific') {
                          setItemNotReadyDate(prev => ({ ...prev, [item.id]: '' }));
                        }
                      }}>
                      <option value="">Quick pick...</option>
                      <option value="1w">Within 1 week</option>
                      <option value="2w">Within 2 weeks</option>
                      <option value="30d">Within 30 days</option>
                      <option value="specific">Specific date...</option>
                    </select>
                    {itemNotReadyQuick[item.id] === 'specific' && (
                      <input
                        type="date"
                        style={s.inputField}
                        value={itemNotReadyDate[item.id] || ''}
                        onChange={e => setItemNotReadyDate(prev => ({ ...prev, [item.id]: e.target.value }))}
                      />
                    )}
                    <button
                      style={s.confirmBtn}
                      disabled={isBusy || !itemNotReadyQuick[item.id] || (itemNotReadyQuick[item.id] === 'specific' && !itemNotReadyDate[item.id])}
                      onClick={() => confirmNotReady(item)}>
                      Confirm
                    </button>
                  </div>
                </div>
              )}

              {err && (
                <div style={{ marginTop:'10px', padding:'8px 12px', background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'6px', color:'#dc2626', fontSize:'11px' }}>
                  ⚠ {err}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.header}>
            <img src="/Wigley.png" alt="Wigley" style={{ width:"44px", height:"44px", objectFit:"contain", borderRadius:"10px" }} />
            <div style={s.headerText}>
              <div style={s.headerTitle}>WigleyHub · NTRG</div>
              <div style={s.headerSub}>Document Request</div>
            </div>
          </div>

          <div style={s.body}>
            {/* Client greeting */}
            <div style={s.clientBadge}>
              <div style={s.clientName}>{tokenData?.client_name || 'Secure Document Upload'}</div>
              <div style={s.clientSub}>🔒 Secure link{tokenData?.expires_at ? ` · Expires ${new Date(tokenData.expires_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}` : ''}</div>
            </div>

            {/* Intro */}
            <div style={s.reqIntro}>NTRG is requesting the following documents</div>
            <div style={s.reqTitle}>Action needed</div>
            <div style={s.reqSub}>For each document below, <strong>upload the file</strong> using the drop zone. If you can't upload right now, choose one of the alternatives: <strong>Already Provided</strong> (sent outside this request), <strong>N/A</strong> (doesn't apply), or <strong>Not Ready</strong> (with an estimated date).</div>

            {/* Property card */}
            <div style={s.propertyCard}>
              <div style={s.propertyName}>{requestData.property_name}</div>
              <div style={s.propertyMeta}>
                MPID {requestData.master_property_id} · Tax Year {requestData.tax_year}
                {requestData.request_code && (
                  <> · <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontWeight: '700', color: '#956f1d' }}>{requestData.request_code}</span></>
                )}
              </div>
              <span style={s.progressPill}>{resolvedItems} of {totalItems} resolved{notReadyItems > 0 ? ` · ${notReadyItems} not ready` : ''}</span>
            </div>

            {/* Items */}
            {requestItems.map(item => renderItem(item))}

            {/* Completion state */}
            {allResolvedOrScheduled && (
              <div style={{ marginTop:'16px', padding:'18px 20px', background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'10px', textAlign:'center' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:'#166534', marginBottom:'4px' }}>✓ All items addressed</div>
                <div style={{ fontSize:'12px', color:'#4a5068' }}>
                  NTRG has everything they need from you right now. You can close this window.
                </div>
              </div>
            )}

            <div style={s.disclaimer}>
              🔒 Your files are encrypted in transit and stored securely.<br/>
              This link is private and specific to your account with NTRG.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // GENERIC MODE — original UI, unchanged (for non-request tokens)
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <img src="/Wigley.png" alt="Wigley" style={{ width:"44px", height:"44px", objectFit:"contain", borderRadius:"10px" }} />
          <div style={s.headerText}>
            <div style={s.headerTitle}>WigleyHub · NTRG</div>
            <div style={s.headerSub}>{isConsultant ? 'Consultant Document Upload' : 'Secure Document Upload'}</div>
          </div>
        </div>
        <div style={s.body}>
          <div style={s.clientBadge}>
            {isConsultant ? (
              <>
                <div style={{ fontSize:'11px', fontWeight:'700', color: '#60a5fa', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>🏛 Consultant Upload</div>
                <div style={s.clientName}>{tokenData?.consultant_firm || tokenData?.client_name || 'Consultant Upload'}</div>
                {tokenData?.client_name && tokenData?.consultant_firm && (
                  <div style={s.clientSub}>📋 For: {tokenData.client_name}</div>
                )}
              </>
            ) : (
              <>
                <div style={s.clientName}>{tokenData?.client_name || 'Client Document Upload'}</div>
                {tokenData?.property_name && <div style={s.clientSub}>📍 {tokenData.property_name}</div>}
              </>
            )}
            <div style={s.clientSub}>🔒 Secure link{tokenData?.expires_at ? ` · Expires ${new Date(tokenData.expires_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}` : ''}</div>
          </div>

          <p style={{ fontSize:'13px', color:'#4a5068', lineHeight:'1.6', marginBottom:'20px' }}>
            {isConsultant
              ? 'Please upload any documents, notices, or records below. Files will be securely transmitted directly to NTRG.'
              : 'Please upload your documents below. Your files will be securely transmitted directly to NTRG.'}
          </p>

          <input ref={fileInputRef} type="file" multiple style={{ display:'none' }} onChange={handleFileSelect} />
          <div
            style={s.dropZone(dragOver)}
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <div style={s.dropIcon}>📄</div>
            <div style={s.dropTitle}>Drop files here or click to browse</div>
            <div style={s.dropSub}>PDF, Excel, Word, images — any format accepted</div>
          </div>

          {files.length > 0 && !uploading && (
            <div style={s.fileList}>
              {files.map((f, i) => (
                <div key={i} style={s.fileItem}>
                  <span style={{ fontSize:'18px' }}>
                    {f.name.endsWith('.pdf') ? '📄' : f.name.match(/\.(xlsx?|csv)$/i) ? '📊' : f.name.match(/\.(docx?)$/i) ? '📝' : '📎'}
                  </span>
                  <span style={s.fileName}>{f.name}</span>
                  <span style={s.fileSize}>{formatSize(f.size)}</span>
                  <button style={s.removeBtn} onClick={()=>removeFile(i)}>×</button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div style={{ marginBottom:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', color:'var(--slate)', marginBottom:'6px' }}>
                <span>Uploading {progress.current}...</span>
                <span>{progress.done} of {progress.total} files</span>
              </div>
              <div style={{ background:'var(--line)', borderRadius:'6px', height:'8px', overflow:'hidden' }}>
                <div style={{ height:'100%', background:'linear-gradient(90deg,#9C0000,#B40000)', borderRadius:'6px', width:`${progress.total ? (progress.done/progress.total)*100 : 0}%`, transition:'width 0.3s' }} />
              </div>
              <div style={{ fontSize:'11px', color:'var(--slate)', marginTop:'6px', textAlign:'center' }}>
                Please keep this window open until the upload completes
              </div>
            </div>
          )}

          {!uploading && (
            <div style={{ marginBottom:'4px' }}>
              <label style={s.commentLabel}>
                💬 Comments <span style={{ fontWeight:'400', color:'#9ca3af' }}>(optional)</span>
              </label>
              <textarea
                style={s.commentBox}
                placeholder="Add any notes or context about these files for the NTRG team..."
                value={comment}
                onChange={e => setComment(e.target.value)}
              />
            </div>
          )}

          {uploading ? (
            <button style={s.disabledBtn} disabled>⏳ Uploading {progress.done} of {progress.total}...</button>
          ) : files.length > 0 ? (
            <button style={s.uploadBtn} onClick={uploadFiles}>
              ⬆ Upload {files.length} File{files.length !== 1 ? 's' : ''} Securely
            </button>
          ) : (
            <button style={s.disabledBtn} disabled>Select files to upload</button>
          )}

          <div style={s.disclaimer}>
            🔒 Your files are encrypted in transit and stored securely.<br/>
            {isConsultant
              ? 'This link is private and specific to your consultant account with NTRG.'
              : 'This link is private and specific to your account with NTRG.'}
          </div>
        </div>
      </div>
    </div>
  );
}
